import React, { useEffect, useState, useRef } from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import ModalDelete from '../../components/modal-delete/modal-delete';
import printerProfileApi from '../../services/printerProfileApi';
import categoryApi from '../../services/categoryApi';
import storeApi from '../../services/storeApi';
import menuApi from '../../services/menuApi';
import { capitalizeWords } from '../../utils/baseUtils';
import {
  PageHeader,
  TWButton,
  TWInput,
  TWSelect,
  TWModal,
  TWDataTable,
  EmptyState,
} from '../../components/ui';
import {
  headerCls,
  EditAction,
  DeleteAction,
  DataTableSkeleton,
} from '../../data/columns';
import {
  byId,
  profileCategoryLabels,
  profileHasNoActiveCategories,
  sortedCategoryOptions,
} from './printer-profiles.labels';

const DeviceManagement = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [profileList, setProfileList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [menuList, setMenuList] = useState([]);
  const [categoriesFailed, setCategoriesFailed] = useState(false);
  const [deletedProfile, setDeletedProfile] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    categoriesLoading: false,
    storesLoading: false,
    menusLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    categoryids: [],
    storeid: '',
  });
  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });
  const [errors, setErrors] = useState({
    name: false,
    categoryids: false,
    storeid: false,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });
  const cancelButtonRef = useRef(null);

  const getProfileList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setProfileList([]);
    try {
      const query = `?companyid=${globalCompany.id}&PageNumber=${pagination.pageNumber}&PageSize=${pagination.pageSize}&isdeleted=false`;
      const list = await printerProfileApi.list(query);
      if (list.data.data?.length > 0) {
        setProfileList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getCategoryList = async () => {
    setLoading((prev) => ({ ...prev, categoriesLoading: true }));
    setCategoriesFailed(false);
    try {
      const query = `companyid=${globalCompany.id}&pageSize=1000&isdeleted=false`;
      const list = await categoryApi.list(query);
      if (list.data.data) setCategoryList(list.data.data);
    } catch (error) {
      setCategoriesFailed(true);
    } finally {
      setLoading((prev) => ({ ...prev, categoriesLoading: false }));
    }
  };

  const getStoreList = async () => {
    setLoading((prev) => ({ ...prev, storesLoading: true }));
    try {
      const query = `companyid=${globalCompany.id}&pageSize=1000&isdeleted=false`;
      const list = await storeApi.list(query);
      if (list.data.data) setStoreList(list.data.data);
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, storesLoading: false }));
    }
  };

  const getMenuList = async () => {
    setLoading((prev) => ({ ...prev, menusLoading: true }));
    try {
      const list = await menuApi.list(`?companyid=${globalCompany.id}`);
      if (list.data.data) setMenuList(list.data.data);
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, menusLoading: false }));
    }
  };

  useEffect(() => {
    if (globalCompany.id) {
      getProfileList();
      getCategoryList();
      getStoreList();
      getMenuList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber, globalCompany.id]);

  const onFormChange = (val, key) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const onClickAdd = () => {
    setForm({ id: '', name: '', categoryids: [], storeid: '' });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (profile) => {
    setForm({
      id: profile.id,
      name: profile.name,
      categoryids: profile.categoryids || [],
      storeid: profile.storeid,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.name || !form.storeid || form.categoryids.length === 0) {
      setErrors({
        name: !form.name,
        storeid: !form.storeid,
        categoryids: form.categoryids.length === 0,
      });
      return;
    }
    setErrors({ name: false, storeid: false, categoryids: false });

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = {
        ...form,
        name: form.name ? capitalizeWords(form.name.trim()) : form.name,
      };
      await printerProfileApi.upsert(body);
    } catch (error) {
      // silent
    } finally {
      await getProfileList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({ name: false, storeid: false, categoryids: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (profile) => {
    setDeletedProfile({ name: profile.name, id: profile.id });
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await printerProfileApi.deleteById(deletedProfile.id);
    } catch (error) {
      // silent
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getProfileList();
    }
  };

  const getStoreName = (storeid) =>
    storeList.find((s) => s.id === storeid)?.name || '-';

  const menusById = byId(menuList);
  const categoriesById = byId(categoryList);
  const getCategoryNames = (categoryids) =>
    profileCategoryLabels(categoryids, categoriesById, menusById);
  const categoryOptions = sortedCategoryOptions(categoryList, menusById);

  const columns = [
    {
      title: <span className={headerCls}>Profile Name</span>,
      key: 'name',
      render: (_, profile) => (
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {profile.name}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Store</span>,
      key: 'store',
      render: (_, profile) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {getStoreName(profile.storeid)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Categories</span>,
      key: 'categories',
      render: (_, profile) =>
        !loading.categoriesLoading &&
        !categoriesFailed &&
        profileHasNoActiveCategories(profile.categoryids, categoriesById) ? (
          <span className="block max-w-md whitespace-normal text-sm text-amber-700 dark:text-amber-400">
            No active categories. Edit the profile to choose them again.
          </span>
        ) : (
          <span className="block max-w-md whitespace-normal text-sm text-slate-600 dark:text-slate-300">
            {getCategoryNames(profile.categoryids)}
          </span>
        ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 96,
      render: (_, profile) => (
        <div className="inline-flex gap-1">
          <EditAction onClick={() => onClickEdit(profile)} />
          <DeleteAction onClick={() => onClickDelete(profile)} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="bg-white p-4 dark:bg-slate-800 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHeader
            title="Printer Profiles"
            description="A list of all the printer profiles in your account."
            actions={
              <TWButton
                variant="primary"
                icon={PlusIcon}
                label="Add Printer Profile"
                onClick={onClickAdd}
              />
            }
          />

          {loading.listLoading ? (
            <DataTableSkeleton
              headers={[
                { label: 'Profile Name', w: 'w-40' },
                { label: 'Store', w: 'w-32' },
                { label: 'Categories', w: 'w-48' },
                { align: 'right', w: 'w-20', shape: 'circle' },
              ]}
            />
          ) : profileList.length === 0 ? (
            <EmptyState
              title="No printer profiles"
              description="Add your first printer profile to get started."
            />
          ) : (
            <div className="reveal">
              <TWDataTable
                columns={columns}
                data={profileList}
                rowKey="id"
                pagination={
                  pagination.totalRecords > pagination.pageSize
                    ? {
                        current: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        total: pagination.totalRecords,
                        showTotal: (total, range) =>
                          `Showing ${range[0]} to ${range[1]} of ${total} profiles`,
                        onChange: (page) =>
                          setPagination((prev) => ({
                            ...prev,
                            pageNumber: page,
                          })),
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit Printer Profile' : 'Add Printer Profile'}
        size="xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex w-full items-center justify-between">
            <div>
              {form.id && (
                <TWButton
                  variant="danger"
                  icon={TrashIcon}
                  label="Delete"
                  loading={loading.deleteLoading}
                  disabled={loading.deleteLoading}
                  onClick={() => onClickDelete(form)}
                />
              )}
            </div>
            <div className="flex gap-2">
              <TWButton
                ref={cancelButtonRef}
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseUpsert}
              />
              <TWButton
                variant="primary"
                label={form.id ? 'Save Changes' : 'Add Printer Profile'}
                loading={loading.upsertLoading}
                disabled={loading.upsertLoading}
                onClick={onFormSubmit}
              />
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <TWInput
            label="Printer Profile Name"
            required
            value={form.name}
            onChange={(e) => onFormChange(e.target.value, 'name')}
            placeholder="Kitchen Printer"
            maxLength={50}
            error={
              errors.name ? 'Printer Profile Name can not be empty' : undefined
            }
          />
          <TWSelect
            label="Store"
            required
            loading={loading.storesLoading}
            placeholder="Please select Store"
            value={form.storeid || undefined}
            onChange={(val) => onFormChange(val, 'storeid')}
            options={storeList?.map((x) => ({ label: x.name, value: x.id }))}
            error={errors.storeid ? 'Store can not be empty' : undefined}
          />
          <TWSelect
            label="Categories"
            required
            mode="multiple"
            loading={loading.categoriesLoading || loading.menusLoading}
            placeholder="Please select Categories"
            value={form.categoryids}
            onChange={(val) => onFormChange(val, 'categoryids')}
            options={categoryOptions}
            error={
              errors.categoryids
                ? 'At least one category must be selected'
                : undefined
            }
          />
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Printer Profile?"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedProfile}
      />
    </div>
  );
};

export default DeviceManagement;
